import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  createAdminTemplate,
  getAdminTemplate,
  previewCompileTemplate,
  publishAdminTemplate,
  updateAdminTemplate,
  type CompiledPromptResult,
  type CreateTemplatePayload,
  type GenerationArtifactType,
  type TemplateDomain,
  type TemplateStatus,
  type TemplateTaskType,
} from '@/services/templateSystemApi';
import { getUserFriendlyMessage } from '@/services/apiError';
import FormMessage from '@/components/common/FormMessage';
import ContentApiErrorState from '@/components/common/ContentErrorState';

const outputTargetOptions: GenerationArtifactType[] = [
  'lesson_plan_markdown',
  'assessment_set',
  'topology_spec',
  'slide_spec',
  'slides_html',
  'slides_beamer_tex',
];

const domainOptions: TemplateDomain[] = ['python', 'information_systems', 'networks', 'iot'];
const taskTypeOptions: TemplateTaskType[] = ['lesson_plan', 'question_set', 'topology', 'slides', 'mixed'];
const statusOptions: TemplateStatus[] = ['draft', 'published', 'archived'];

interface FormState {
  slug: string;
  name: string;
  description: string;
  domain: TemplateDomain;
  taskType: TemplateTaskType;
  status: TemplateStatus;
  tags: string;
  inputSchemaJsonText: string;
  uiSchemaJsonText: string;
  outputTargetsJson: GenerationArtifactType[];
  systemPrompt: string;
  planningPrompt: string;
  generationPrompt: string;
  sampleInputJsonText: string;
}

const defaultFormState: FormState = {
  slug: '',
  name: '',
  description: '',
  domain: 'networks',
  taskType: 'mixed',
  status: 'draft',
  tags: '',
  inputSchemaJsonText: JSON.stringify(
    {
      type: 'object',
      required: ['topic', 'gradeLevel', 'durationMinutes'],
      properties: {
        topic: { type: 'string', title: 'Topic' },
        gradeLevel: { type: 'string', enum: ['Grade 7', 'Grade 8', 'Grade 9'] },
        durationMinutes: { type: 'number', default: 45 },
      },
    },
    null,
    2,
  ),
  uiSchemaJsonText: JSON.stringify({ order: ['topic', 'gradeLevel', 'durationMinutes'] }, null, 2),
  outputTargetsJson: ['lesson_plan_markdown'],
  systemPrompt: '',
  planningPrompt: '',
  generationPrompt: '',
  sampleInputJsonText: JSON.stringify({ topic: '校园网络', gradeLevel: 'Grade 8', durationMinutes: 45 }, null, 2),
};

export default function AdminTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isCreateMode = !id;
  const navigate = useNavigate();

  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewResult, setPreviewResult] = useState<CompiledPromptResult | null>(null);

  const loadTemplateDetail = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setLoadError(null);
    setError('');

    try {
      const detail = await getAdminTemplate(id);
      setFormState({
        slug: detail.slug,
        name: detail.name,
        description: detail.description,
        domain: detail.domain,
        taskType: detail.taskType,
        status: detail.status,
        tags: (detail.tagsJson ?? []).join(', '),
        inputSchemaJsonText: JSON.stringify(detail.inputSchemaJson, null, 2),
        uiSchemaJsonText: detail.uiSchemaJson ? JSON.stringify(detail.uiSchemaJson, null, 2) : '',
        outputTargetsJson: detail.outputTargetsJson,
        systemPrompt: detail.systemPrompt,
        planningPrompt: detail.planningPrompt,
        generationPrompt: detail.generationPrompt,
        sampleInputJsonText: JSON.stringify({ topic: '校园网络', gradeLevel: 'Grade 8', durationMinutes: 45 }, null, 2),
      });
    } catch (unknownError) {
      setLoadError(unknownError);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    void loadTemplateDetail();
  }, [id, loadTemplateDetail]);

  const isSeedTemplate = useMemo(
    () =>
      formState.slug.includes('middle-school-family-network-lesson-kit') ||
      formState.slug.includes('information-system-intro-lesson-plan') ||
      formState.slug.includes('smart-home-iot-activity-pack') ||
      formState.slug.includes('python-loop-practice-pack'),
    [formState.slug],
  );

  const parseJsonFields = () => {
    const inputSchemaJson = JSON.parse(formState.inputSchemaJsonText) as Record<string, unknown>;
    const uiSchemaJson = formState.uiSchemaJsonText.trim()
      ? (JSON.parse(formState.uiSchemaJsonText) as Record<string, unknown>)
      : undefined;
    const sampleInputJson = JSON.parse(formState.sampleInputJsonText) as Record<string, unknown>;

    return {
      inputSchemaJson,
      uiSchemaJson,
      sampleInputJson,
    };
  };

  const buildPayload = () => {
    const { inputSchemaJson, uiSchemaJson } = parseJsonFields();
    const payload: CreateTemplatePayload = {
      slug: formState.slug,
      name: formState.name,
      description: formState.description,
      domain: formState.domain,
      taskType: formState.taskType,
      status: formState.status,
      tagsJson: formState.tags
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      inputSchemaJson,
      uiSchemaJson,
      outputTargetsJson: formState.outputTargetsJson,
      systemPrompt: formState.systemPrompt,
      planningPrompt: formState.planningPrompt,
      generationPrompt: formState.generationPrompt,
    };

    return payload;
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    try {
      setSaving(true);
      const payload = buildPayload();

      if (id) {
        await updateAdminTemplate(id, payload);
        setSuccess('模板已更新');
      } else {
        const created = await createAdminTemplate(payload);
        setSuccess('模板已创建');
        navigate(`/admin/templates/${created.id}`);
      }
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '保存失败，请检查 JSON 格式与必填项'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewCompile = async () => {
    setError('');
    try {
      setPreviewing(true);
      const { sampleInputJson } = parseJsonFields();
      const result = await previewCompileTemplate({
        systemPrompt: formState.systemPrompt,
        planningPrompt: formState.planningPrompt,
        generationPrompt: formState.generationPrompt,
        sampleInputJson,
        outputTargetsJson: formState.outputTargetsJson,
      });
      setPreviewResult(result);
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '预览编译失败，请检查 sample input JSON'));
    } finally {
      setPreviewing(false);
    }
  };

  const handlePublish = async () => {
    if (!id) {
      return;
    }

    const accepted = window.confirm('确认发布当前模板吗？发布后会生成不可变版本快照。');
    if (!accepted) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      setPublishing(true);
      const result = await publishAdminTemplate(id);
      setSuccess(`发布成功，当前版本 v${result.version.version}`);
      setFormState((prev) => ({
        ...prev,
        status: result.template.status,
      }));
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '发布失败'));
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className='max-w-[1080px] mx-auto px-6 py-12 text-apple-gray'>加载中…</div>;
  }

  if (!isCreateMode && loadError) {
    return (
      <div className='max-w-[1080px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={loadError} onRetry={() => void loadTemplateDetail()} />
      </div>
    );
  }

  return (
    <div className='max-w-[1200px] mx-auto px-6 py-12'>
      <div className='flex items-center justify-between gap-4 mb-6'>
        <div>
          <h1
            className='font-semibold text-apple-near-black mb-2'
            style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            {isCreateMode ? '新建模板' : '编辑模板'}
          </h1>
          <p className='text-apple-gray text-[0.9375rem]'>管理 prompt、输入 schema、输出目标与发布版本。</p>
        </div>
        <div className='flex gap-2'>
          <Link to='/admin/templates' className='btn btn-secondary'>
            返回列表
          </Link>
          {!isCreateMode ? (
            <button type='button' className='btn btn-primary' disabled={publishing} onClick={() => void handlePublish()}>
              {publishing ? '发布中…' : '发布模板'}
            </button>
          ) : null}
        </div>
      </div>

      {isSeedTemplate ? (
        <div className='mb-4'>
          <FormMessage tone='success' message='当前模板匹配内置 Seed 模板，可直接作为起点进行二次编辑。' />
        </div>
      ) : null}

      {(error || success) && (
        <div className='space-y-2 mb-4'>
          {error ? <FormMessage tone='error' message={error} /> : null}
          {success ? <FormMessage tone='success' message={success} /> : null}
        </div>
      )}

      <div className='grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6'>
        <form className='bg-white rounded-2xl p-6 shadow-card-sm space-y-4' onSubmit={handleSave}>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            <LabeledInput
              id='template-name'
              label='名称'
              value={formState.name}
              onChange={(value) => setFormState((prev) => ({ ...prev, name: value }))}
            />
            <LabeledInput
              id='template-slug'
              label='Slug'
              value={formState.slug}
              onChange={(value) => setFormState((prev) => ({ ...prev, slug: value }))}
            />
          </div>

          <LabeledTextarea
            id='template-description'
            label='描述'
            value={formState.description}
            onChange={(value) => setFormState((prev) => ({ ...prev, description: value }))}
          />

          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <LabeledSelect
              id='template-domain'
              label='Domain'
              value={formState.domain}
              options={domainOptions}
              onChange={(value) => setFormState((prev) => ({ ...prev, domain: value as TemplateDomain }))}
            />
            <LabeledSelect
              id='template-taskType'
              label='Task Type'
              value={formState.taskType}
              options={taskTypeOptions}
              onChange={(value) => setFormState((prev) => ({ ...prev, taskType: value as TemplateTaskType }))}
            />
            <LabeledSelect
              id='template-status'
              label='Status'
              value={formState.status}
              options={statusOptions}
              onChange={(value) => setFormState((prev) => ({ ...prev, status: value as TemplateStatus }))}
            />
          </div>

          <LabeledInput
            id='template-tags'
            label='Tags（逗号分隔）'
            value={formState.tags}
            onChange={(value) => setFormState((prev) => ({ ...prev, tags: value }))}
          />

          <fieldset className='space-y-2'>
            <legend className='text-[0.875rem] font-medium text-apple-near-black'>输出目标</legend>
            <div className='grid grid-cols-2 gap-2'>
              {outputTargetOptions.map((target) => {
                const checked = formState.outputTargetsJson.includes(target);
                return (
                  <label key={target} className='inline-flex items-center gap-2 text-[0.8125rem] text-apple-near-black'>
                    <input
                      type='checkbox'
                      checked={checked}
                      onChange={(event) => {
                        setFormState((prev) => {
                          const next = new Set(prev.outputTargetsJson);
                          if (event.target.checked) {
                            next.add(target);
                          } else {
                            next.delete(target);
                          }
                          return {
                            ...prev,
                            outputTargetsJson: Array.from(next),
                          };
                        });
                      }}
                    />
                    {target}
                  </label>
                );
              })}
            </div>
          </fieldset>

          <LabeledTextarea
            id='template-input-schema'
            label='Input Schema JSON'
            value={formState.inputSchemaJsonText}
            minHeight='min-h-56'
            onChange={(value) => setFormState((prev) => ({ ...prev, inputSchemaJsonText: value }))}
          />

          <LabeledTextarea
            id='template-ui-schema'
            label='UI Schema JSON（可选）'
            value={formState.uiSchemaJsonText}
            minHeight='min-h-36'
            onChange={(value) => setFormState((prev) => ({ ...prev, uiSchemaJsonText: value }))}
          />

          <LabeledTextarea
            id='template-system-prompt'
            label='System Prompt'
            value={formState.systemPrompt}
            minHeight='min-h-28'
            onChange={(value) => setFormState((prev) => ({ ...prev, systemPrompt: value }))}
          />

          <LabeledTextarea
            id='template-planning-prompt'
            label='Planning Prompt'
            value={formState.planningPrompt}
            minHeight='min-h-28'
            onChange={(value) => setFormState((prev) => ({ ...prev, planningPrompt: value }))}
          />

          <LabeledTextarea
            id='template-generation-prompt'
            label='Generation Prompt'
            value={formState.generationPrompt}
            minHeight='min-h-36'
            onChange={(value) => setFormState((prev) => ({ ...prev, generationPrompt: value }))}
          />

          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? '保存中…' : '保存模板'}
          </button>
        </form>

        <aside className='bg-white rounded-2xl p-6 shadow-card-sm space-y-3 h-fit'>
          <h2 className='font-semibold text-[1.0625rem] text-apple-near-black'>Prompt 预览编译</h2>

          <LabeledTextarea
            id='template-sample-input'
            label='Sample Input JSON'
            value={formState.sampleInputJsonText}
            minHeight='min-h-36'
            onChange={(value) => setFormState((prev) => ({ ...prev, sampleInputJsonText: value }))}
          />

          <button type='button' className='btn btn-secondary' disabled={previewing} onClick={() => void handlePreviewCompile()}>
            {previewing ? '编译中…' : 'Preview Compile'}
          </button>

          {previewResult ? (
            <div className='space-y-3'>
              <PreviewBlock title='Resolved System Prompt' content={previewResult.resolvedSystemPrompt} />
              <PreviewBlock title='Resolved Planning Prompt' content={previewResult.resolvedPlanningPrompt} />
              <PreviewBlock title='Resolved Generation Prompt' content={previewResult.resolvedGenerationPrompt} />
              <p className='text-[0.75rem] text-apple-gray'>
                Target schema: {previewResult.targetSchemaName} · targets: {previewResult.targetArtifactTypes.join(', ')}
              </p>
            </div>
          ) : (
            <p className='text-[0.8125rem] text-apple-gray'>输入 sample JSON 后可预览最终拼接 prompt。</p>
          )}
        </aside>
      </div>
    </div>
  );
}

interface LabeledInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function LabeledInput({ id, label, value, onChange }: LabeledInputProps) {
  return (
    <div className='space-y-1.5'>
      <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={id}>
        {label}
      </label>
      <input id={id} className='input' value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

interface LabeledSelectProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function LabeledSelect({ id, label, value, options, onChange }: LabeledSelectProps) {
  return (
    <div className='space-y-1.5'>
      <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={id}>
        {label}
      </label>
      <select id={id} className='input' value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  );
}

interface LabeledTextareaProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  minHeight?: string;
}

function LabeledTextarea({ id, label, value, onChange, minHeight = 'min-h-24' }: LabeledTextareaProps) {
  return (
    <div className='space-y-1.5'>
      <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor={id}>
        {label}
      </label>
      <textarea id={id} className={`input ${minHeight}`} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

interface PreviewBlockProps {
  title: string;
  content: string;
}

function PreviewBlock({ title, content }: PreviewBlockProps) {
  return (
    <div>
      <p className='text-[0.8125rem] font-medium text-apple-near-black mb-1'>{title}</p>
      <pre className='text-[0.75rem] text-apple-near-black bg-apple-light-gray rounded-lg p-3 overflow-auto whitespace-pre-wrap max-h-48'>
        {content || '(empty)'}
      </pre>
    </div>
  );
}
